/**
 * EdenDate red packets — send panel + in-chat cards.
 */
import I18n from '@lib/langPack';
import {getTrConfig} from '@/edenTranslate';
import {refreshWallet} from '@/edenWallet';

const API = '/edenapi/api';
const MARK = '#EDENRP#';

function t(key: string, fallback?: string): string {
  try {
    const out = (I18n as any).format(key, true);
    if(typeof out === 'string' && out !== key) return out;
  } catch(e) {}
  return fallback || key;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) {
  const e = document.createElement(tag);
  if(cls) e.className = cls;
  if(text !== undefined) e.textContent = text;
  return e;
}

function myId(): number {
  try {
    // eslint-disable-next-line
    const rs = (window as any).rootScope;
    return Number(rs && rs.myId) || 0;
  } catch(e) {
    return 0;
  }
}

function peerIdFromChat(): number {
  // most reliable: the composer carries data-peer-id
  const input = document.querySelector('.input-message-input[data-peer-id]') as HTMLElement;
  if(input?.dataset.peerId) return Number(input.dataset.peerId) || 0;

  const bubble = document.querySelector('.bubble[data-peer-id]') as HTMLElement;
  if(bubble?.dataset.peerId) return Number(bubble.dataset.peerId) || 0;

  const title = document.querySelector('.peer-title[data-peer-id]') as HTMLElement;
  if(title?.dataset.peerId) return Number(title.dataset.peerId) || 0;

  const hash = (location.hash || '').replace('#', '').trim();
  if(hash && /^-?\d+$/.test(hash)) return Number(hash);

  return 0;
}

function setStatus(el: HTMLElement | null, text: string) {
  if(!el) { try { console.warn('[edenRp]', text); } catch(e) {} return; }
  el.textContent = text;
}

// ---------------------------------------------------------------- send panel
export function openRedPacketPanel() {
  const overlay = el('div', 'eden-modal-overlay');
  const card = el('div', 'eden-modal');
  card.appendChild(el('div', 'eden-modal-title', t('Eden.Rp.Title', '发送红包')));

  const amount = el('input', 'eden-input') as HTMLInputElement;
  amount.type = 'number';
  amount.min = '1';
  amount.step = '0.01';
  amount.placeholder = t('Eden.Rp.Amount', '金额（U，最少 1U）');

  const count = el('input', 'eden-input') as HTMLInputElement;
  count.type = 'number';
  count.min = '1';
  count.value = '1';
  count.placeholder = t('Eden.Rp.Count', '个数');

  const mode = el('select', 'eden-input') as HTMLSelectElement;
  [['single', t('Eden.Rp.Single', '单人全额')], ['random', t('Eden.Rp.Random', '拼手气随机')], ['targeted', t('Eden.Rp.Targeted', '定向红包')]]
    .forEach(([v, label]) => {
      const o = el('option', undefined, label);
      o.value = v;
      mode.appendChild(o);
    });

  const target = el('input', 'eden-input') as HTMLInputElement;
  target.placeholder = t('Eden.Rp.TargetUser', '定向用户 ID（可选）');
  target.style.display = 'none';

  mode.addEventListener('change', () => {
    target.style.display = mode.value === 'targeted' ? '' : 'none';
    count.style.display = mode.value === 'single' ? 'none' : '';
  });

  const send = el('button', 'eden-chip eden-chip-primary', t('Eden.Send', '发送'));
  const cancel = el('button', 'eden-chip', t('Eden.Close', '关闭'));
  const status = el('div', 'eden-modal-text', '');

  card.appendChild(amount);
  card.appendChild(count);
  card.appendChild(mode);
  card.appendChild(target);
  card.appendChild(status);

  const footer = el('div', 'eden-modal-actions');
  footer.appendChild(cancel);
  footer.appendChild(send);
  card.appendChild(footer);

  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => { if(e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);

  cancel.addEventListener('click', () => overlay.remove());

  send.addEventListener('click', async () => {
    const total = Number(amount.value);
    const cnt = mode.value === 'single' ? 1 : Math.max(1, Number(count.value) || 1);
    if(!total || total < 1) { setStatus(status, t('Eden.Rp.Min', '最少 1U')); return; }

    const chatId = peerIdFromChat();
    if(!chatId) { setStatus(status, t('Eden.Rp.NoChat', '请先打开一个聊天')); return; }

    send.setAttribute('disabled', '1');
    setStatus(status, '...');
    try {
      const res = await fetch(`${API}/redpacket/create`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          sender_id: myId(),
          chat_id: chatId,
          chat_type: 0,
          total,
          count: cnt,
          mode: mode.value,
          target_user: target.value ? Number(target.value) : null
        })
      });
      const out = await res.json();
      if(!out || !out.ok) {
        setStatus(status, (out && out.error) || 'failed');
        send.removeAttribute('disabled');
        return;
      }
      overlay.remove();
      refreshWallet();
      await sendMarker(out.marker);
    } catch(e) {
      setStatus(status, String(e).slice(0, 80));
      send.removeAttribute('disabled');
    }
  });
}

async function sendMarker(marker: string) {
  // put the marker into the composer and press send (reuses tweb's own pipeline)
  const field =
    (document.querySelector('.input-message-input[contenteditable="true"]') as HTMLElement) ||
    (document.querySelector('.chat-input [contenteditable="true"]') as HTMLElement);
  const btn = document.querySelector('.btn-send, .new-message-wrapper .btn-send') as HTMLElement;
  if(!field) { console.warn('[edenRp] composer not found'); return; }
  field.textContent = marker;
  field.dispatchEvent(new InputEvent('input', {bubbles: true}));
  setTimeout(() => { btn?.click(); }, 150);
}

// ---------------------------------------------------------------- cards
function buildCard(rpId: number, meta?: any) {
  const card = el('div', 'eden-rp-card');
  const icon = el('div', 'eden-rp-icon', '🧧');
  const body = el('div', 'eden-rp-body');
  const title = el('div', 'eden-rp-title', t('Eden.Rp.Card', '领取红包'));
  const sub = el('div', 'eden-rp-sub', meta ? `${meta.remaining} / ${meta.total} U` : '');
  body.appendChild(title);
  body.appendChild(sub);
  const btn = el('button', 'eden-chip eden-chip-primary', t('Eden.Rp.Claim', '领取'));
  card.appendChild(icon);
  card.appendChild(body);
  card.appendChild(btn);

  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    btn.setAttribute('disabled', '1');
    btn.textContent = '...';
    try {
      const res = await fetch(`${API}/redpacket/claim`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({id: rpId, user_id: myId()})
      });
      const out = await res.json();
      if(out && out.ok) {
        btn.textContent = `+${out.amount} U`;
        btn.classList.add('eden-chip-ok');
        refreshWallet();
      } else {
        btn.textContent = (out && out.error) || 'error';
        btn.removeAttribute('disabled');
      }
    } catch(err) {
      btn.removeAttribute('disabled');
    }
  });

  fetch(`${API}/redpacket/${rpId}`).then((r) => r.json()).then((d) => {
    if(d && d.ok) sub.textContent = `${d.remaining} / ${d.total} U · ${d.remaining_count}/${d.count}`;
  }).catch(() => {});

  return card;
}

function scan(root: ParentNode) {
  root.querySelectorAll('.bubble:not([data-eden-rp])').forEach((b) => {
    const el0 = b as HTMLElement;
    const text = el0.innerText || '';
    const m = text.match(/#EDENRP#(\d+)/);
    if(!m) return;
    el0.dataset.edenRp = '1';
    const id = Number(m[1]);
    const host = el0.querySelector('.message, .text-content') as HTMLElement;
    if(!host) return;
    host.textContent = '';
    host.appendChild(buildCard(id));
  });
}

export function startRedPacketScan() {
  scan(document);
  const obs = new MutationObserver((ms) => {
    ms.forEach((mu) => mu.addedNodes.forEach((n) => {
      if(n.nodeType === 1) scan(n as HTMLElement);
    }));
  });
  obs.observe(document.body, {childList: true, subtree: true});
}

export function injectRedPacketButton() {
  const attach = document.querySelector('.attach-file');
  if(!attach || document.querySelector('.eden-rp-btn')) return;
  const btn = el('button', 'eden-rp-btn', '🧧');
  btn.title = t('Eden.Rp.Title', '发送红包');
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    openRedPacketPanel();
  });
  attach.parentElement?.insertBefore(btn, attach);
}
